import {ReactElement} from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {TreeViewItem, TreeViewProps} from '../../../@types/tree-view-item';
import {TreeView} from '@mui/x-tree-view/TreeView';
import PsfCategoryActionsMenu from '../menus/PsfCategory.actions.menu';
import {PsfCategory} from '../../../@types/psfTemplate';

export interface CategoryTreeViewProps extends TreeViewProps {
    addSubCategory: (parent: PsfCategory|null, category: string) => void;
    renameCategory: (category: PsfCategory) => void;
    deleteCategory: (category: PsfCategory) => void;
}

export function PsfCategoryTreeView(props: CategoryTreeViewProps): ReactElement {
    const { title, nodes, exploreMode, onClickItem, addSubCategory, renameCategory, deleteCategory, ...other } = props;

    const handleSingleClick = (event: any): void => {
        onClickItem(event);
    }

    const getExistingNames = (node: TreeViewItem): string[] => {
        const index = node.groupPath?.indexOf('::');
        if (index && index > 0) { //given node is a sub node
            const names = node.groupPath?.split('::');
            let children = nodes;
            names!.forEach((name, nameIndex) => {
                const parent = children?.find((parent) => parent.name == name);
                if (nameIndex != names!.length - 1) {//not the selected node itself
                    children = parent?.children ?? [];
                }
            })
            return children.filter((child) => child.name != node.name).map((child) => child.name);
        } else {
            return nodes.map((n) => n.name).filter((name) => name != node.name) ?? [];
        }
    }

    const renderTreeItem = (node: TreeViewItem): ReactElement => {
        return (
            <TreeItem tabIndex={0}  key={node.name + '-' + node.id} nodeId={node.id} label={
                <Box tabIndex={0} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}
                    onClick={handleSingleClick}>
                    <Typography id={node.id} variant="body2" sx={{ flexGrow: 1 }}>{node.name}</Typography>
                    {!exploreMode && <PsfCategoryActionsMenu
                        category={{index: +node.id, name: node.name, path: node.groupPath} as PsfCategory}
                        existingNames={getExistingNames(node)}
                        existingSubs={node.children?.map((n) => n.name)}
                        addSubCategory={addSubCategory}
                        renameCategory={renameCategory}
                        deleteCategory={deleteCategory} /> }
                </Box>
            }
            icon={!node.children && node.hasChildren ? <ChevronRightIcon /> : null}
            >
                {Array.isArray(node.children) ? node.children?.map((child) => renderTreeItem(child)) : null}
            </TreeItem>
        );
    }

    return (
        <>
            {title? <Typography sx={{ mr: 1}} variant='h6' color='primary'>{title}</Typography> : null}
            <TreeView
                aria-label="tree-view"
                defaultCollapseIcon={<ExpandMoreIcon aria-label='collapse icon' />}
                defaultExpandIcon={<ChevronRightIcon aria-label='expand icon'/>}
                sx={{ mt: '20px', flexGrow: 1, overflowY: 'auto', width: '100%', overflowX: 'hidden' }}
                {...other}
            >
                {nodes.map((node) => renderTreeItem(node))}
            </TreeView>
        </>
    );
}